import { Fragment, useContext } from 'react';
import { ChevronDown } from '@doist/icons/brand';
import { ActivityComplete, DateOverdue, Projects, TaskCircle } from '@doist/icons/product-ui';
import { Divider, Image, Text } from '@doist/marketist';
import cn from 'clsx';
import { ProductUICategoryPill } from 'components/product-ui/product-ui-category-pill/product-ui-category-pill';
import { ProductUIBasicListItem } from 'components/product-ui/product-ui-list-item';
import { useProductUINSTranslation } from 'components/product-ui/product-ui-ns-context';
import { ProductUIThemeContext } from 'components/product-ui/product-ui-theme-context';
import css from './product-ui-team-activity.module.css';
import type { ProductUITheme } from 'components/product-ui/product-ui-types';
function getGraphColors(theme: ProductUITheme) {
  switch (theme) {
    case 'red':
      return {
        background: 'ffefe5',
        line: 'e34432'
      };
    case 'yellow':
      return {
        background: 'fff5db',
        line: 'c77b00'
      };
    case 'green':
      return {
        background: 'f0f6df',
        line: '4c7a45'
      };
    case 'seaSalt':
      return {
        background: 'ebf5f0',
        line: '497d7e'
      };
  }
}
export type TeamActivityProps = {
  sidebar: React.ReactNode;
  projectTitle: string;
  tasks: React.ReactNode[];
  activeTasks: number;
  completedTasks: number;
  overdueTasks: number;
};
export function TeamActivity({
  sidebar,
  projectTitle,
  tasks,
  activeTasks,
  completedTasks,
  overdueTasks
}: TeamActivityProps) {
  const {
    t
  } = useProductUINSTranslation();
  const theme = useContext(ProductUIThemeContext);
  const {
    background,
    line
  } = getGraphColors(theme);
  return <div className={css.teamActivity} data-sentry-component="TeamActivity" data-sentry-source-file="product-ui-team-activity.tsx">
            {sidebar}
            <div className={css.content}>
                <div className={cn(css.contentHeader, 'pb-4 pi-4 mb-12 mi-12')}>
                    <Projects color="var(--productui-primary-theme-color)" width={25} height={25} data-sentry-element="Projects" data-sentry-source-file="product-ui-team-activity.tsx" />
                    <Text size="minor-lg" weight="semibold" data-sentry-element="Text" data-sentry-source-file="product-ui-team-activity.tsx">
                        {projectTitle}
                    </Text>
                    <ChevronDown width={16} height={16} data-sentry-element="ChevronDown" data-sentry-source-file="product-ui-team-activity.tsx" />
                </div>
                <Image src={`https://res.cloudinary.com/imagist/image/upload/e_replace_color:${line}:10:4C7A45/e_replace_color:${background}:1:F0F6DF/v1726473563/team-activity-graph_2x_kyjeyg.png`} className="mis-24" width={276} height={165} alt="" data-sentry-element="Image" data-sentry-source-file="product-ui-team-activity.tsx" />
                <div className={cn(css.categories, 'mi-24 mbs-8 pis-12')}>
                    <ProductUICategoryPill startIcon={<TaskCircle />} variant="primary" data-sentry-element="ProductUICategoryPill" data-sentry-source-file="product-ui-team-activity.tsx">
                        {t('sequence.teamActivity.active', {
            count: activeTasks
          })}
                    </ProductUICategoryPill>
                    <ProductUICategoryPill startIcon={<ActivityComplete />} variant="outline" data-sentry-element="ProductUICategoryPill" data-sentry-source-file="product-ui-team-activity.tsx">
                        {t('sequence.teamActivity.completed', {
            count: completedTasks
          })}
                    </ProductUICategoryPill>
                    <ProductUICategoryPill startIcon={<DateOverdue />} variant="outline" data-sentry-element="ProductUICategoryPill" data-sentry-source-file="product-ui-team-activity.tsx">
                        {t('sequence.teamActivity.overdue', {
            count: overdueTasks
          })}
                    </ProductUICategoryPill>
                </div>
                <div className="mi-32 mbs-24">
                    <Divider data-sentry-element="Divider" data-sentry-source-file="product-ui-team-activity.tsx" />
                    {tasks.map((task, index) => <Fragment key={`task-${index}`}>
                            {task}
                            <Divider />
                        </Fragment>)}
                </div>
            </div>
        </div>;
}
TeamActivity.Sidebar = function TeamActivitySidebar({
  title,
  collaborators
}: {
  title: string;
  collaborators: React.ReactNode[];
}) {
  const {
    t
  } = useProductUINSTranslation();
  return <div className={css.sidebar}>
            <Text className={cn(css.sidebarTitle, 'mbe-8')} size="minor-lg" weight="semibold">
                {title}
            </Text>
            <ProductUIBasicListItem icon="group" label={t('sequence.teamActivity.sidebar.all')} active />
            <div className={cn(css.sidebarCollaborators, 'mbs-24')}>
                <Text size="minor-base" weight="semibold">
                    {t('sequence.teamActivity.sidebar.collaborators')}
                </Text>
                <ProductUIBasicListItem icon="group" label={t('sequence.teamActivity.sidebar.unassigned')} />
                {collaborators}
            </div>
        </div>;
};